import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  title?: string;
  autoClose?: boolean;
  duration?: number;
}

export interface Modal {
  id: string;
  title: string;
  content: string | React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  hideCancel?: boolean;
}

export interface UiState {
  sidebarOpen: boolean;
  darkMode: boolean;
  notifications: Notification[];
  modal: Modal | null;
  currentPage: string;
}

// Initial state
const initialState: UiState = {
  sidebarOpen: true,
  darkMode: false,
  notifications: [],
  modal: null,
  currentPage: 'dashboard',
};

// Helper function to generate unique ID for notifications
const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
    },
    setDarkMode: (state, action: PayloadAction<boolean>) => {
      state.darkMode = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification = {
        ...action.payload,
        id: generateId(),
        autoClose: action.payload.autoClose ?? true,
        duration: action.payload.duration ?? 5000,
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    showModal: (state, action: PayloadAction<Omit<Modal, 'id'>>) => {
      state.modal = {
        ...action.payload,
        id: generateId(),
      };
    },
    hideModal: (state) => {
      state.modal = null;
    },
    setCurrentPage: (state, action: PayloadAction<string>) => {
      state.currentPage = action.payload;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleDarkMode,
  setDarkMode,
  addNotification,
  removeNotification,
  clearNotifications,
  showModal,
  hideModal,
  setCurrentPage,
} = uiSlice.actions;

export default uiSlice.reducer;